import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import type { ApiError, ShiftReport } from '../api';
import { boxOfficeEndpoints, endpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Panel, Table, formatWhen } from '../ui';
import { INPUT, dollars } from './event/shared';

/**
 * Cash drawer reconciliation (spec §38): every shift with its expected
 * total built from the payments, refunds and cash movements it owns, the
 * count, the over/short, and a manager's correction when a count was wrong.
 */
export default function ShiftsPage() {
  const { id } = useParams();
  const can = usePermissions();
  if (!can('reports.view_financial')) return <NotPermitted permission="reports.view_financial" />;
  return id ? <ShiftDetail id={id} /> : <ShiftList />;
}

function ShiftList() {
  const [venueId, setVenueId] = useState('');
  const [date, setDate] = useState('');
  const venues = useQuery({ queryKey: ['venues'], queryFn: endpoints.venues });
  const shifts = useQuery({ queryKey: ['shifts', venueId, date], queryFn: () => boxOfficeEndpoints.shifts({ ...(venueId ? { venue_id: venueId } : {}), ...(date ? { date } : {}) }) });
  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold text-ink-900">Drawer shifts</h1>
      <Panel
        title="Shifts"
        description="Expected = float + cash sales − cash refunds + paid-ins − paid-outs. Over/short posts to Cash Over/Short at close."
        actions={
          <div className="flex gap-2">
            <select className={INPUT} value={venueId} onChange={(e) => setVenueId(e.target.value)} aria-label="Venue">
              <option value="">All venues</option>
              {(venues.data?.venues ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
            <input type="date" className={INPUT} value={date} onChange={(e) => setDate(e.target.value)} aria-label="Business date" />
          </div>
        }
      >
        <Table headers={['Date', 'Register · drawer', 'Cashier', 'Float', 'Expected', 'Counted', 'Over / short', 'Status']}>
          {(shifts.data?.shifts ?? []).length === 0 ? <EmptyRow columns={8}>No shifts.</EmptyRow> : null}
          {(shifts.data?.shifts ?? []).map((s) => (
            <tr key={s.id} className="border-b border-line last:border-0">
              <td className="px-5 py-3 text-sm">
                <Link to={`/box-office/shifts/${s.id}`} className="text-accent underline-offset-2 hover:underline">
                  {s.business_date}
                </Link>
                <div className="text-xs text-ink-400">{formatWhen(s.opened_at)}</div>
              </td>
              <td className="px-5 py-3 text-sm">
                {s.register_name} · {s.drawer_name}
              </td>
              <td className="px-5 py-3 text-sm">{s.cashier_name}</td>
              <td className="tabular px-5 py-3 text-sm">{dollars(s.opening_float_cents)}</td>
              <td className="tabular px-5 py-3 text-sm">{s.expected_close_cents === null ? '—' : dollars(s.expected_close_cents)}</td>
              <td className="tabular px-5 py-3 text-sm">{s.counted_close_cents === null ? '—' : dollars(s.counted_close_cents)}</td>
              <td className="tabular px-5 py-3 text-sm">{s.over_short_cents === null ? '—' : <span className={s.over_short_cents === 0 ? 'text-ok' : 'text-bad'}>{s.over_short_cents === 0 ? 'balanced' : `${s.over_short_cents < 0 ? 'short' : 'over'} ${dollars(Math.abs(s.over_short_cents))}`}</span>}</td>
              <td className="px-5 py-3">
                <Badge tone={s.status === 'OPEN' ? 'accent' : 'neutral'}>{s.status}</Badge>
                {s.corrected_at ? <Badge tone="warn">corrected</Badge> : null}
              </td>
            </tr>
          ))}
        </Table>
      </Panel>
    </div>
  );
}

function ShiftDetail({ id }: { id: string }) {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const report = useQuery<ShiftReport, ApiError>({ queryKey: ['shift', id], queryFn: () => boxOfficeEndpoints.shift(id) });
  const correct = useMutation({
    mutationFn: ({ cents, reason }: { cents: number; reason: string }) => boxOfficeEndpoints.correctShift(id, { counted_close_cents: cents, reason }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['shift', id] });
      void queryClient.invalidateQueries({ queryKey: ['shifts'] });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  if (report.error) return <ErrorNote>{report.error.message}</ErrorNote>;
  const data = report.data;
  if (!data) return null;
  const { shift, expected } = data;
  return (
    <div className="space-y-4">
      <div className="flex items-baseline gap-3">
        <Link to="/box-office/shifts" className="text-sm text-accent underline-offset-2 hover:underline">
          ← Drawer shifts
        </Link>
        <h1 className="text-lg font-semibold text-ink-900">
          {data.drawer?.name ?? 'Drawer'} · {data.cashier_name} · {shift.business_date}
        </h1>
        <Badge tone={shift.status === 'OPEN' ? 'accent' : 'neutral'}>{shift.status}</Badge>
      </div>
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <Panel title="Reconciliation" description="Every figure below is the sum of the rows further down, so the report ties to the payments by construction.">
        <div className="grid gap-4 px-5 py-4 md:grid-cols-2">
          <dl className="space-y-1 text-sm">
            {[
              ['Opening float', expected.opening_float_cents],
              [`Cash sales (${expected.cash_sale_count})`, expected.cash_sales_cents],
              [`Cash refunds (${expected.cash_refund_count})`, -expected.cash_refunds_cents],
              ['Paid in', expected.paid_in_cents],
              ['Paid out', -expected.paid_out_cents],
            ].map(([label, cents]) => (
              <div key={String(label)} className="flex justify-between">
                <dt className="text-ink-600">{label}</dt>
                <dd className="tabular">{dollars(Number(cents))}</dd>
              </div>
            ))}
            <div className="flex justify-between border-t border-line pt-1 font-semibold">
              <dt>Expected</dt>
              <dd className="tabular">{dollars(expected.expected_cents)}</dd>
            </div>
            {shift.status === 'CLOSED' ? (
              <>
                <div className="flex justify-between">
                  <dt className="text-ink-600">Counted{shift.original_counted_close_cents !== null ? ` (first count ${dollars(shift.original_counted_close_cents)})` : ''}</dt>
                  <dd className="tabular">{dollars(shift.counted_close_cents)}</dd>
                </div>
                <div className="flex justify-between font-semibold">
                  <dt>Over / short</dt>
                  <dd className={`tabular ${shift.over_short_cents === 0 ? 'text-ok' : 'text-bad'}`}>{shift.over_short_cents === 0 ? 'balanced' : `${(shift.over_short_cents ?? 0) < 0 ? 'short' : 'over'} ${dollars(Math.abs(shift.over_short_cents ?? 0))}`}</dd>
                </div>
                {shift.correction_reason ? <p className="text-xs text-warn">Corrected {formatWhen(shift.corrected_at)}: {shift.correction_reason}</p> : null}
              </>
            ) : null}
          </dl>
          {shift.status === 'CLOSED' && can('box_office.drawer.correct') ? (
            <form
              className="space-y-2 text-sm"
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const cents = Math.round(Number((form.elements.namedItem('counted') as HTMLInputElement).value) * 100);
                const reason = (form.elements.namedItem('reason') as HTMLInputElement).value.trim();
                if (reason) correct.mutate({ cents, reason });
              }}
            >
              <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Correct the count</p>
              <input name="counted" type="number" step="0.01" min="0" defaultValue={((shift.counted_close_cents ?? 0) / 100).toFixed(2)} className={`${INPUT} w-full`} aria-label="Corrected count" />
              <input name="reason" placeholder="Why the count was wrong" className={`${INPUT} w-full`} aria-label="Reason" />
              <Button type="submit" disabled={correct.isPending}>
                Post correction
              </Button>
            </form>
          ) : null}
        </div>
      </Panel>
      <Panel title="Cash sales">
        <Table headers={['Order', 'Amount', 'Tendered', 'Change', 'When']}>
          {data.payments.length === 0 ? <EmptyRow columns={5}>No cash sales.</EmptyRow> : null}
          {data.payments.map((p) => (
            <tr key={p.id} className="border-b border-line last:border-0">
              <td className="px-5 py-2 font-mono text-sm">
                <Link to={`/orders/${p.order_id}`} className="text-accent hover:underline">
                  {p.order_number}
                </Link>
              </td>
              <td className="tabular px-5 py-2 text-sm">{dollars(p.amount_cents)}</td>
              <td className="tabular px-5 py-2 text-sm">{dollars(p.cash_tendered_cents)}</td>
              <td className="tabular px-5 py-2 text-sm">{dollars(p.change_given_cents)}</td>
              <td className="px-5 py-2 text-xs text-ink-600">{formatWhen(p.captured_at)}</td>
            </tr>
          ))}
        </Table>
      </Panel>
      <Panel title="Cash refunds and movements">
        <Table headers={['What', 'Reference', 'Amount', 'When']}>
          {data.refunds.length + data.movements.length === 0 ? <EmptyRow columns={4}>None.</EmptyRow> : null}
          {data.refunds.map((r) => (
            <tr key={r.id} className="border-b border-line last:border-0">
              <td className="px-5 py-2 text-sm">Cash refund</td>
              <td className="px-5 py-2 font-mono text-sm">
                {r.refund_number} · {r.order_number}
              </td>
              <td className="tabular px-5 py-2 text-sm">−{dollars(r.amount_cents)}</td>
              <td className="px-5 py-2 text-xs text-ink-600">{formatWhen(r.completed_at)}</td>
            </tr>
          ))}
          {data.movements.map((m) => (
            <tr key={m.id} className="border-b border-line last:border-0">
              <td className="px-5 py-2 text-sm">{m.movement_type.replaceAll('_', ' ').toLowerCase()}</td>
              <td className="px-5 py-2 text-sm text-ink-600">
                {m.reason} · {m.staff_name}
              </td>
              <td className="tabular px-5 py-2 text-sm">
                {m.direction === 'OUT' ? '−' : ''}
                {dollars(m.amount_cents)}
              </td>
              <td className="px-5 py-2 text-xs text-ink-600">{formatWhen(m.created_at)}</td>
            </tr>
          ))}
        </Table>
      </Panel>
    </div>
  );
}
